<script setup lang="ts">
import { useHead } from "@unhead/vue"
import { ArrowRight, ChevronsDown, Pause, Play } from "lucide-vue-next"
import { Motion } from "motion-v"
import { computed, onMounted, ref } from "vue"
import { useRoute } from "vue-router"
import { ReservationDialogV2 } from "~/components/reservation"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { useBottomBarVisibility } from "~/composables/bottomBar"
import { useConcept } from "~/composables/concept"
import { useMedia } from "~/composables/media"
import { scrollToAndFocus } from "~/composables/scroll"
import { kebabCase, titleCase } from "~/lib/case"
import { cn } from "~/lib/utils"


function handleScrollToLocations(event: Event) {
    event.preventDefault()
    scrollToAndFocus("locations")
}

const { isDesktop } = useMedia()
const { concept } = useConcept()
const route = useRoute()
useBottomBarVisibility()
const prefersReducedMotion = ref(false)

const muxPlayerRef = ref<HTMLElement | null>(null)
const isPaused = ref(false)

const togglePlay = () => {
    const muxPlayer = muxPlayerRef.value as any
    if (muxPlayer) {
        if (muxPlayer.paused) {
            muxPlayer.play().catch(() => {
                console.warn("Play was prevented")
            })
            isPaused.value = false
        } else {
            muxPlayer.pause()
            isPaused.value = true
        }
    }
}

onMounted(() => {
    document.documentElement.style.setProperty(
        "--hero-height", 
        `${window.innerHeight}px`
    )
    prefersReducedMotion.value = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const script = document.createElement("script")
    script.src = concept.value?.external?.mux?.src || "https://cdn.jsdelivr.net/npm/@mux/mux-video"
    script.async = true
    document.head.appendChild(script)

    script.onload = () => {
        setTimeout(() => {
            const muxPlayer = muxPlayerRef.value as any
            if (!muxPlayer) return

            isPaused.value = muxPlayer.paused

            muxPlayer.addEventListener("play", () => {
                isPaused.value = false
            })

            muxPlayer.addEventListener("pause", () => {
                isPaused.value = true
            })

            const handleFirstTouch = (event: TouchEvent) => {
                const playPauseButton = document.getElementById("play-pause-button")
                const touchTarget = event.target as Element
                
                if (playPauseButton && (touchTarget === playPauseButton || playPauseButton.contains(touchTarget))) {
                    return
                }
                
                if (muxPlayer.paused) {
                    muxPlayer.play().catch(() => {
                        console.warn("Autoplay blocked by browser for mux-video")
                    })
                }
                document.removeEventListener("touchstart", handleFirstTouch)
            }

            document.addEventListener("touchstart", handleFirstTouch, { once: true })
        }, 500)
    }
})

const ogImagePath = computed(() => concept.value?.locations?.[0]?.ogImage)
const metaImage = computed(() => {
    return concept.value?.website + ogImagePath.value
})
const metaTitle = `${titleCase(concept.value?.name)} | ${concept.value?.tagline}` 

const metaSiteName = titleCase(concept.value?.name)

if (route.path === "/") {
    useHead({
        title: metaTitle,
        meta: [
            { name: "description", content: concept.value?.tagline },
            { property: "og:title", content: metaTitle },
            { property: "og:site_name", content: metaSiteName },
            { property: "og:description", content: concept.value?.tagline },
            { property: "og:url", content: concept.value?.website },
            { property: "og:type", content: "website" },
            { property: "og:image", content: metaImage },
            { name: "twitter:card", content: "summary_large_image" },
            { name: "twitter:title", content: metaTitle },
            { name: "twitter:description", content: concept.value?.tagline },
            { name: "twitter:image", content: metaImage }
        ],
        link: [
            {
                rel: "canonical",
                href: concept.value?.website
            }
        ],
        script: [
            {
                type: "application/ld+json",
                innerHTML: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": metaSiteName,
                    "url": concept.value?.website
                })
            }
        ]
    })
}

const locations = computed(() => concept.value?.locations ?? [])

const uniqueStates = computed(() => {
    const states = new Set<string>()
    for (const loc of locations.value) {
        if (loc.address?.state) states.add(loc.address.state)
    }
    return [...states]
})

const hasMultipleStates = computed(() => uniqueStates.value.length > 1)

const locationsByState = computed(() => {
    const map = new Map<string, typeof locations.value>()
    for (const loc of locations.value) {
        const state = loc.address?.state
        if (!state) continue
        if (!map.has(state)) map.set(state, [])
        map.get(state)!.push(loc)
    }
    return map
})

const stateAbbreviations: Record<string, string> = {
    CA: "California",
    TX: "Texas"
}
</script>

<template>  
    <div class="w-full overflow-hidden flex flex-col">
        <section 
            class="relative flex flex-col items-center justify-center overflow-hidden w-full"
            style="height: var(--hero-height, 100svh);"
        >
            <mux-video
                v-if="concept.external?.mux && !prefersReducedMotion"
                ref="muxPlayerRef"
                :playback-id="concept.external?.mux?.playbackId"
                :metadata-video-title="concept.external?.mux?.metadataVideoTitle"
                :poster="concept.external?.mux?.poster"
                class="absolute top-0 left-0 w-full brightness-75 [--media-object-fit:cover] [--media-object-position:center] pointer-events-none"
                style="height: var(--hero-height, 100vh);"
                autoplay
                muted
                loop
                playsinline
                aria-hidden="true"
            />
            <video
                v-else-if="concept.video && !prefersReducedMotion"
                :src="concept.video"
                class="absolute top-0 left-0 w-full object-cover brightness-75"
                style="height: var(--hero-height, 100vh);"
                autoplay
                muted
                loop
                aria-hidden="true"
            >
                <video
                    kind="captions"
                    src="data:text/vtt;base64,V0VCVlRUCg=="
                    srclang="en"
                    label="No audio"
                    default
                />
            </video>
            <ThemeImg
                v-else
                :src="concept.images.hero || concept.images.wordmark || ''"
                alt="Hero background image"
                class="absolute top-0 left-0 w-full object-cover brightness-75"
                :style="'height: var(--hero-height, 100vh);'"
            />
            <div class="absolute inset-0 hero-gradient" />
            
            <div class="self-center w-hero-section-wordmark-container-width-small md:w-hero-section-wordmark-container-width-medium px-4 md:px-6 z-20">
                <div class="flex flex-col items-center justify-center">
                    <h1 class="sr-only">
                        {{ titleCase(concept.name) }}
                    </h1>
                    <img
                        :src="concept.images.wordmark"
                        :alt="`${titleCase(concept.name)} wordmark logo`"
                        class="w-full max-w-sm h-auto"
                        width="384"
                        height="128"
                        fetchpriority="high"
                        decoding="async"
                    >
                </div>
            </div>
            <div
                :class="cn('absolute bottom-20 left-1/2 -translate-x-1/2 z-20', isDesktop && 'hidden')"
            >
                <Motion
                    as="div"
                    class="flex justify-center"
                    :variants="{
                        hidden: { y: 25, opacity: 0 },
                        visible: { y: 0, opacity: 1 }
                    }"
                    initial="hidden"
                    while-in-view="visible"
                    :in-view-options="{ once: true }"
                    :transition="{
                        type: 'spring',
                        duration: 1.5,
                        delay: 0.5
                    }"
                >
                    <ReservationDialogV2>
                        <ThemeButton
                            size="lg"
                            tabindex="0"
                            class="px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                        >
                            <span>Reservations</span>
                        </ThemeButton>
                    </ReservationDialogV2>
                </Motion>
            </div>
            <ThemeButton
                v-if="concept.external?.mux && !prefersReducedMotion"
                id="play-pause-button"
                tabindex="0"
                class="absolute left-4 bottom-4 z-50 bg-black/25 p-3 cursor-pointer hover:bg-black/50 transition pointer-events-auto rounded-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                :aria-label="isPaused ? 'Play video' : 'Pause video'"
                variant="ghost"
                @click="togglePlay"
            >
                <Pause
                    v-if="!isPaused"
                    class="text-foreground"
                    aria-hidden="true"
                    :size="16"
                />
                <Play
                    v-else
                    class="text-foreground"
                    aria-hidden="true"
                    :size="16"
                />
            </ThemeButton>
            <Motion
                v-if="isDesktop"
                as="div"
                class="absolute bottom-4 hidden md:flex w-full justify-center z-20"
                :variants="{
                    hidden: { opacity: 0 },
                    visible: { opacity: 1 }
                }"
                initial="hidden"
                while-in-view="visible"
                :in-view-options="{ once: true }"
                :transition="{
                    type: 'spring',
                    delay: 2,
                    duration: 1.5
                }"
            >
                <a
                    href="#locations"
                    tabindex="0"
                    class="flex animate-pulse rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                    aria-label="Scroll to locations section"
                    @click="handleScrollToLocations"
                    @keydown.enter="handleScrollToLocations"
                    @keydown.space.prevent="handleScrollToLocations"
                >
                    <ChevronsDown class="size-7" />
                </a>
            </Motion>
        </section>
        <section
            id="locations"
            tabindex="-1"
            class="space-y-10 md:space-y-12 w-full max-w-8xl mx-auto px-4 md:px-6 py-16 md:py-24"
        >
            <h2
                v-if="!hasMultipleStates"
                class="text-3xl md:text-4xl font-headline text-center"
                :aria-label="`${titleCase(concept.name)} Locations`"
            >
                Locations
            </h2>
            <template v-if="hasMultipleStates">
                <div
                    v-for="[state, stateLocations] of locationsByState"
                    :key="state"
                    class="space-y-6"
                >
                    <h2 class="text-2xl md:text-3xl font-headline text-center py-8">
                        {{ stateAbbreviations[state] }}
                    </h2>
                    <Motion
                        as="ul"
                        class="flex flex-col md:flex-row flex-wrap items-center justify-center gap-4 md:gap-6"
                        initial="hidden"
                        while-in-view="visible"
                        :in-view-options="{ once: true }"
                        :transition="{
                            staggerChildren: 0.25
                        }"
                    >
                        <Motion
                            v-for="location of stateLocations"
                            :key="location.name"
                            as="li"
                            class="w-full md:w-[calc(25%-1.125rem)] relative group"
                            :variants="{
                                hidden: { y: 25, opacity: 0 },
                                visible: { y: 0, opacity: 1 }
                            }"
                            :transition="{
                                type: 'spring',
                                duration: 1.5
                            }"
                        >
                            <RouterLink
                                :to="`/${kebabCase(location.path)}`"
                                tabindex="0"
                                class="block cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                                :aria-label="`Navigate to ${titleCase(concept.name)} ${titleCase(location.name)} restaurant page`"
                            >
                                <div class="overflow-hidden rounded-md">
                                    <ThemeImg
                                        :src="location.image"
                                        :alt="`${titleCase(concept.name)} ${titleCase(location.name)} restaurant exterior`"
                                        class="w-full aspect-square object-cover brightness-50 opacity-75 transition-all ease-in-out duration-700 group-hover:brightness-100 group-hover:scale-105"
                                        loading="eager"
                                    />
                                </div>
                                <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                    <ThemeButton
                                        size="lg"
                                        variant="ghost"
                                        class="text-sm md:text-base lg:text-lg uppercase bg-foreground/10 group-hover:bg-foreground group-hover:text-background px-6"
                                        as="div"
                                    >
                                        <span>{{ titleCase(concept.name) }} {{ titleCase(location.name) }}</span>
                                        <div class="w-0 overflow-hidden transition-all duration-700 group-hover:w-4">
                                            <ArrowRight class="size-4 opacity-0 transition-opacity duration-1000 group-hover:opacity-100" />
                                        </div>
                                    </ThemeButton>
                                </div>
                            </RouterLink>
                        </Motion>
                    </Motion>
                </div>
            </template>
            <Motion
                v-else
                as="ul"
                class="flex flex-col md:flex-row flex-wrap items-center justify-center gap-4 md:gap-6"
                initial="hidden"
                while-in-view="visible"
                :in-view-options="{ once: true }"
                :transition="{
                    staggerChildren: 0.25
                }"
            >
                <Motion
                    v-for="location of locations"
                    :key="location.name"
                    as="li"
                    class="w-full md:w-[calc(33%-1.125rem)] relative group"
                    :variants="{
                        hidden: { y: 25, opacity: 0 },
                        visible: { y: 0, opacity: 1 }
                    }"
                    :transition="{
                        type: 'spring',
                        duration: 1.5
                    }"
                >
                    <RouterLink
                        :to="`/${kebabCase(location.path)}`"
                        tabindex="0"
                        class="block cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                        :aria-label="`Navigate to ${titleCase(concept.name)} ${titleCase(location.name)} restaurant page`"
                    >
                        <div class="overflow-hidden rounded-md">
                            <ThemeImg
                                :src="location.image"
                                :alt="`${titleCase(concept.name)} ${titleCase(location.name)} restaurant exterior`"
                                class="w-full aspect-square object-cover brightness-50 opacity-75 transition-all ease-in-out duration-700 group-hover:brightness-100 group-hover:scale-105"
                                loading="eager"
                            />
                        </div>
                        <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <ThemeButton
                                size="lg"
                                variant="ghost"
                                class="text-sm md:text-base lg:text-lg uppercase bg-foreground/10 group-hover:bg-foreground group-hover:text-background px-6"
                                as="div"
                            >
                                <span>{{ titleCase(concept.name) }} {{ titleCase(location.name) }}</span>
                                <div class="w-0 overflow-hidden transition-all duration-700 group-hover:w-4">
                                    <ArrowRight class="size-4 opacity-0 transition-opacity duration-1000 group-hover:opacity-100" />
                                </div>
                            </ThemeButton>
                        </div>
                    </RouterLink>
                </Motion>
            </Motion>
        </section>

        <!-- Merch is intentionally hidden on Jemma's concept page; it still renders on the location page. -->
        <section
            v-if="concept.merch && concept.name !== 'jemma'"
            id="merch"
            tabindex="-1"
            class="relative flex flex-col items-center justify-center overflow-hidden w-full min-h-[50svh] md:min-h-[60svh]"
        >
            <ThemeImg
                src="/global/merch/concept.jpg"
                :alt="concept.merch.imageAlt || concept.merch.title || ''"
                class="absolute inset-0 w-full h-full object-cover brightness-50"
            />
            <div class="absolute inset-0 bg-black/40" />
            <Motion
                as="div"
                class="relative z-10 flex flex-col items-center justify-center gap-6 px-4 md:px-6 text-center"
                :variants="{
                    hidden: { y: 25, opacity: 0 },
                    visible: { y: 0, opacity: 1 }
                }"
                initial="hidden"
                while-in-view="visible"
                :in-view-options="{ once: true }"
                :transition="{
                    type: 'spring',
                    duration: 1.5
                }"
            >
                <h2 class="text-3xl md:text-4xl font-headline">
                    {{ concept.merch.title }}
                </h2>
                <a
                    :href="concept.merch.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    tabindex="0"
                    :aria-label="concept.merch.buttonText"
                >
                    <ThemeButton
                        size="lg"
                        class="px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                        <span>{{ concept.merch.buttonText }}</span>
                    </ThemeButton>
                </a>
            </Motion>
        </section>
    </div>
</template>